/**
 * Brand Brain projection layer — PURE functions over a BrandBrain value.
 *
 * No new Store-interface methods: persistence of the BrandBrain document
 * rides the SHIPPED `db` store exactly as-is (a JSON-safe `BrandBrain`
 * value inside an existing collection record), and every read/query/write
 * here is an immutable in-memory projection the caller then persists.
 * That keeps this layer tenant-agnostic and trivially testable — the same
 * functions work over a local snapshot, a Firestore doc, or a fixture.
 */

import type {
  BrandBrain,
  BrandEntry,
  BrandEntryKind,
  BrandScope,
  BrandStatus,
} from './schema';

export interface BrandEntryFilter {
  scope?: BrandScope;
  kind?: BrandEntryKind;
  status?: BrandStatus;
  /** Case-insensitive substring match on title + body. */
  query?: string;
}

/** Stable ordering everywhere: weight desc, then id asc (tie-break). */
export function compareBrandEntries(a: BrandEntry, b: BrandEntry): number {
  if (b.weight !== a.weight) return b.weight - a.weight;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/** True when the entry routes to the given scope ('all' matches always). */
export function entryMatchesScope(entry: BrandEntry, scope: BrandScope): boolean {
  return entry.scopes.includes('all') || entry.scopes.includes(scope);
}

/**
 * Filter + sort entries. Every filter field is optional; omitted fields
 * match everything. Result is a NEW array in stable order (weight desc,
 * id asc) — the input brain is never mutated.
 */
export function listBrandEntries(
  brain: BrandBrain,
  filter: BrandEntryFilter = {},
): BrandEntry[] {
  const q = filter.query?.trim().toLowerCase();
  return brain.entries
    .filter((e) => {
      if (filter.scope && !entryMatchesScope(e, filter.scope)) return false;
      if (filter.kind && e.kind !== filter.kind) return false;
      if (filter.status && e.status !== filter.status) return false;
      if (q) {
        const hay = `${e.title}\n${e.body}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    })
    .sort(compareBrandEntries);
}

/**
 * Insert or replace an entry by id. IMMUTABLE: returns a new BrandBrain;
 * the input brain (and its entries array) are never mutated.
 */
export function upsertBrandEntry(brain: BrandBrain, entry: BrandEntry): BrandBrain {
  const exists = brain.entries.some((e) => e.id === entry.id);
  const entries = exists
    ? brain.entries.map((e) => (e.id === entry.id ? { ...entry } : e))
    : [...brain.entries, { ...entry }];
  return { entries };
}

/**
 * Archive an entry (soft delete — flips status, never removes the row so
 * history and provenance stay intact). No-op when the id is unknown.
 * IMMUTABLE: returns a new BrandBrain.
 */
export function archiveBrandEntry(brain: BrandBrain, id: string): BrandBrain {
  return {
    entries: brain.entries.map((e) =>
      e.id === id ? { ...e, status: 'archived' as const } : e,
    ),
  };
}

/**
 * The composition read: every ACTIVE entry routed to `scope`, including
 * 'all'-scoped entries, in stable weight-desc order. This is the exact set
 * `composeBrandPrompt` selects from.
 */
export function getActiveByScope(brain: BrandBrain, scope: BrandScope): BrandEntry[] {
  return listBrandEntries(brain, { scope, status: 'active' });
}
